import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Users, Play, Copy, Check, Crown, UserPlus, ArrowLeft, Settings, Feather, Sparkles } from 'lucide-react'

interface Player {
  id: string
  name: string
  color: string
  isHost: boolean
  isConnected: boolean
  joinedAt: number
  theme?: string
  themeValidated?: boolean
}

interface GameLobbyProps {
  roomCode: string
  roomName: string
  players: Player[]
  currentPlayer: Player
  maxPlayers: number
  isHost: boolean
  onStartGame: (playerTheme: string) => void
  onLeaveRoom: () => void
  onInvitePlayer?: () => void
  onAddBot?: (difficulty?: 'EASY' | 'MEDIUM' | 'HARD') => void
  onThemeUpdate?: (playerId: string, theme: string, validated: boolean) => void
}

import { GAME_CONSTANTS } from '../../utils/constants'

const GameLobby: React.FC<GameLobbyProps> = ({
  roomCode,
  roomName,
  players,
  currentPlayer,
  maxPlayers,
  isHost,
  onStartGame,
  onLeaveRoom,
  onInvitePlayer,
  onAddBot,
  onThemeUpdate
}) => {
  React.useEffect(() => {
    import('react-hot-toast').then(({ toast }) => toast.success('DEBUG: GameLobby MOUNTED'))
  }, [])

  const [copied, setCopied] = useState(false)
  const [showSettings, setShowSettings] = useState(false)

  // 🎮 État pour tracker les bots sélectionnés par l'hôte
  const [selectedBots, setSelectedBots] = useState<string[]>([])

  // Calculer le nombre total de joueurs (Source de vérité = Serveur)
  const totalPlayers = players.length

  // Vérifier si 3 bots identiques sont sélectionnés
  const hasThreeIdenticalBots = React.useMemo(() => {
    const counts: Record<string, number> = {};
    selectedBots.forEach(name => {
      counts[name] = (counts[name] || 0) + 1;
    });
    return Object.values(counts).some(count => count >= 3);
  }, [selectedBots]);

  // Copier le code de la partie
  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(roomCode)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Erreur lors de la copie:', err)
    }
  }

  // Couleurs prédéfinies pour les joueurs
  const playerColors = [
    '#d4af37', // Or (hôte)
    '#e74c3c', // Rouge
    '#3498db', // Bleu
    '#2ecc71'  // Vert
  ]

  return (
    <div className="h-full flex flex-col overflow-hidden font-serif relative">
      {/* Header */}
      <header className="relative z-10 p-6">
        <div className="flex items-center justify-between">
          <button
            onClick={onLeaveRoom}
            className="flex items-center space-x-2 text-[var(--text-primary)] hover:text-[var(--accent-glow)] transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Quitter</span>
          </button>

          <div className="text-center">
            <h1 className="text-2xl font-bold text-[var(--accent-glow)]">RÉMINISCENCE</h1>
            <p className="text-[var(--text-secondary)] text-sm">Salle d'Attente</p>
          </div>

          {isHost && (
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="flex items-center space-x-2 text-[var(--text-primary)] hover:text-[var(--accent-glow)] transition-colors"
            >
              <Settings className="w-5 h-5" />
              <span>Paramètres</span>
            </button>
          )}

          {!isHost && <div className="w-20"></div>}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-4xl">

          {/* Room Info */}
          <motion.div
            className="text-center mb-8"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-[var(--accent-glow)] mb-2">{roomName}</h2>

            {/* Room Code */}
            <div className="flex items-center justify-center space-x-4 mb-4">
              <div className="bg-[var(--glass-bg)] rounded-xl px-6 py-3 border border-[var(--glass-border)]">
                <div className="flex items-center space-x-3">
                  <span className="text-[var(--text-secondary)] text-sm">Code de Partie:</span>
                  <span className="text-[var(--accent-color)] font-mono text-xl font-bold tracking-wider">
                    {roomCode}
                  </span>
                  <button
                    onClick={handleCopyCode}
                    className="p-2 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 transition-colors"
                    title="Copier le code"
                  >
                    {copied ? (
                      <Check className="w-4 h-4 text-green-400" />
                    ) : (
                      <Copy className="w-4 h-4 text-amber-400" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <p className="text-white/60 text-sm">
              Partagez ce code avec vos amis pour qu'ils puissent rejoindre la partie
            </p>
          </motion.div>

          {/* Players Grid */}
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Joueurs connectés */}
            {players.map((player, index) => (
              <motion.div
                key={player.id}
                className="bg-[var(--glass-bg)] rounded-xl p-4 border border-[var(--glass-border)]"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <div className="flex items-center space-x-3">
                  {/* Avatar coloré */}
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center text-[var(--bg-primary)] font-bold text-lg"
                    style={{ backgroundColor: playerColors[index] || '#6b7280' }}
                  >
                    {player.name.charAt(0).toUpperCase()}
                  </div>

                  {/* Infos joueur */}
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h3 className="text-[var(--text-primary)] font-semibold">{player.name}</h3>
                      {player.isHost && (
                        <Crown className="w-4 h-4 text-[var(--accent-color)]" />
                      )}
                      {player.id === currentPlayer.id && (
                        <span className="text-xs bg-[var(--accent-color)]/20 text-[var(--accent-color)] px-2 py-1 rounded-full">
                          Vous
                        </span>
                      )}
                      {player.id.startsWith('bot_') && (
                        <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded-full">
                          🤖 Bot
                        </span>
                      )}
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2">
                        <div
                          className={`w-2 h-2 rounded-full ${player.isConnected ? 'bg-green-400' : 'bg-red-400'
                            }`}
                        />
                        <span className="text-[var(--text-secondary)] text-sm">
                          {player.isConnected ? 'En ligne' : 'Déconnecté'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}

            {/* Slots vides - SYSTÈME ARCHITECTE DE SALLE */}
            {Array.from({ length: maxPlayers - players.length }).map((_, index) => {
              // 🏗️ NOUVEAU: En tant qu'hôte, tu peux configurer TOUS les slots
              const canConfigureSlot = isHost && onAddBot

              // Les 3 adversaires numériques disponibles
              const availableBots = [
                { id: 'gardien', name: 'Le Gardien', icon: '🛡️', difficulty: 'EASY' as const },
                { id: 'archiviste', name: 'L\'Archiviste', icon: '📚', difficulty: 'MEDIUM' as const },
                { id: 'horloger', name: 'L\'Horloger', icon: '⏰', difficulty: 'HARD' as const }
              ]

              // Filtrer les bots déjà présents (simple)
              const usedBotNames = players.filter(p => p.id.startsWith('bot_')).map(p => p.name)
              const availableBotsForSlot = availableBots.filter(bot => !usedBotNames.includes(bot.name))

              return (
                <motion.div
                  key={`empty-${index}`}
                  className="bg-[var(--glass-bg)] rounded-xl p-4 border border-[var(--glass-border)] border-dashed"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: (players.length + index) * 0.1 }}
                >
                  <div className="flex items-center justify-center h-16 text-[var(--text-secondary)]">
                    <div className="text-center">
                      {canConfigureSlot && availableBotsForSlot.length > 0 ? (
                        <>
                          <div className="text-amber-400 mb-2">🎮</div>
                          <select
                            className="bg-gray-700 text-white text-xs rounded px-2 py-1 border border-gray-600"
                            onChange={(e) => {
                              const value = e.target.value

                              if (value === 'human' || value === '') {
                                // Retirer ce slot des bots sélectionnés
                                setSelectedBots(prev => prev.filter((_, i) => i !== index))
                                console.log('👥 Slot configuré pour attendre un ami')
                              } else {
                                const selectedBot = availableBotsForSlot.find(bot => bot.id === value)
                                if (selectedBot) {
                                  // Ajouter ce bot aux sélectionnés
                                  setSelectedBots(prev => {
                                    const newBots = [...prev]
                                    newBots[index] = selectedBot.name
                                    return newBots
                                  })
                                  console.log(`🤖 Bot sélectionné: ${selectedBot.name}`)

                                  // Ajouter le bot au serveur immédiatement avec la bonne difficulté
                                  if (onAddBot) {
                                    onAddBot(selectedBot.difficulty)
                                  }
                                }
                              }
                            }}
                            defaultValue="human"
                          >
                            <option value="human">👥 Attendre un ami</option>
                            <option value="" disabled>──────────────</option>
                            <option value="" disabled>IA (Bots)</option>
                            {availableBotsForSlot.map(bot => (
                              <option key={bot.id} value={bot.id}>
                                {bot.icon} {bot.name}
                              </option>
                            ))}
                          </select>
                        </>
                      ) : (
                        <>
                          <UserPlus className="w-6 h-6 mx-auto mb-1" />
                          <span className="text-sm">En attente d'un joueur</span>
                        </>
                      )}
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </motion.div>

          {/* Player Count */}
          <motion.div
            className="text-center mb-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="inline-flex items-center space-x-2 bg-[var(--glass-bg)] rounded-full px-4 py-2 border border-[var(--glass-border)]">
              <Users className="w-4 h-4 text-[var(--accent-glow)]" />
              <span className="text-[var(--text-primary)] font-medium">
                {totalPlayers} / {maxPlayers} joueurs
              </span>
            </div>
          </motion.div>

          {/* Actions */}
          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            {/* Bouton Inviter (tous les joueurs) */}
            {onInvitePlayer && (
              <button
                onClick={onInvitePlayer}
                className="flex items-center space-x-2 px-6 py-3 bg-[var(--glass-bg)] text-[var(--text-primary)] border border-[var(--glass-border)] rounded-xl hover:bg-white/10 transition-all"
              >
                <UserPlus className="w-5 h-5" />
                <span>Inviter des Amis</span>
              </button>
            )}

            {/* Bouton Démarrer (hôte seulement) */}
            {isHost && (
              <button
                onClick={() => onStartGame('Mes Aventures')}
                className={`flex items-center space-x-2 px-8 py-4 rounded-xl font-bold transition-all duration-300 ${totalPlayers >= GAME_CONSTANTS.MIN_PLAYERS
                  // Keep amber gradient for Start Button as it's a primary action, maybe adapt to accent
                  ? 'bg-gradient-to-r from-[var(--accent-color)] to-[var(--accent-glow)] text-[var(--bg-primary)] hover:scale-105 shadow-lg'
                  : 'bg-gray-500 text-gray-300 cursor-not-allowed'
                  }`}
              >
                <Play className="w-5 h-5" />
                <span>DÉMARRER LA PARTIE</span>
              </button>
            )}

            {/* Message pour les invités */}
            {!isHost && (
              <div className="text-center">
                <p className="text-[var(--text-secondary)] text-sm">
                  En attente que l'hôte démarre la partie...
                </p>
                <div className="flex items-center justify-center space-x-1 mt-2">
                  <div className="w-2 h-2 bg-[var(--accent-color)] rounded-full animate-pulse"></div>
                  <div className="w-2 h-2 bg-[var(--accent-color)] rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                  <div className="w-2 h-2 bg-[var(--accent-color)] rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                </div>
              </div>
            )}
          </motion.div>

          {/* Info pour l'hôte */}
          {(isHost && totalPlayers < GAME_CONSTANTS.MIN_PLAYERS) || (isHost && hasThreeIdenticalBots) ? (
            <motion.div
              className={`mt-6 p-4 rounded-xl border text-center ${hasThreeIdenticalBots
                ? 'bg-red-500/10 border-red-400/30'
                : 'bg-[var(--glass-bg)] border-[var(--glass-border)]'
                }`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              {hasThreeIdenticalBots && (
                <p className="text-red-400 text-sm font-bold mb-2">
                  ⚠️ ATTENTION : 3 bots identiques peuvent créer une boucle temporelle infinie ! (Non recommandé)
                </p>
              )}

              {totalPlayers < GAME_CONSTANTS.MIN_PLAYERS && (
                totalPlayers < 2 ? (
                  <p className="text-[var(--accent-glow)] text-sm">
                    ⚠️ Il faut au minimum 2 joueurs pour démarrer une partie
                  </p>
                ) : (
                  <p className="text-[var(--accent-glow)] text-sm">
                    ⚠ En attente de joueurs...
                  </p>
                )
              )}
            </motion.div>
          ) : null}

          {/* Règles rapides */}
          <motion.div
            className="mt-8 p-4 bg-[var(--glass-bg)] rounded-xl border border-[var(--glass-border)]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1 }}
          >
            <h3 className="text-[var(--text-primary)] font-semibold mb-2 flex items-center">
              <span className="text-[var(--accent-glow)] mr-2">📜</span>
              Rappel des Règles
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-[var(--text-secondary)] text-sm">
              <div>
                <p>• <strong>Objectif:</strong> Restaurer 3 Souvenirs (Fragments)</p>
                <p>• <strong>Action:</strong> Sondez le Cortex adverse</p>
              </div>
              <div>
                <p>• <strong>Attention:</strong> Évitez les Zones d'Ombre !</p>
                <p>• <strong>Climax:</strong> Devinez la phrase pour valider</p>
              </div>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Settings Modal (hôte seulement) */}
      <AnimatePresence>
        {showSettings && isHost && (
          <motion.div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-slate-800 rounded-xl p-6 border border-amber-400/30 max-w-md w-full"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <h3 className="text-white font-bold text-lg mb-4">Paramètres de la Partie</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-white/70 text-sm mb-2">Nom de la Salle</label>
                  <input
                    type="text"
                    value={roomName}
                    readOnly
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white"
                  />
                </div>

                <div>
                  <label className="block text-white/70 text-sm mb-2">Joueurs Maximum</label>
                  <input
                    type="number"
                    value={maxPlayers}
                    readOnly
                    min="2"
                    max="4"
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white"
                  />
                  <p className="text-white/50 text-xs mt-1">
                    Maximum 4 joueurs pour l'interface web
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowSettings(false)}
                  className="px-4 py-2 text-white/70 hover:text-white transition-colors"
                >
                  Fermer
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default GameLobby
